/**
 * Advance detail page — view + edit + status transitions.
 *
 * Il piano di recupero è per-rata: ogni rata ha la sua busta paga (anche non
 * consecutiva, XIII/XIV incluse) e il suo importo.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { fmtEur } from "../lib/format";
import { buildPlanProposals, planLabel } from "../lib/installments";
import { AdvancePdfPreviewDialog } from "../components/AdvancePdfPreviewDialog";
import { BustaPagaSelector } from "../components/BustaPagaSelector";
import {
    InstallmentScheduleEditor,
    buildConsecutiveLabels,
    nextBpLabel,
    evenSplit,
    validateScheduleRows,
    round2,
} from "../components/InstallmentScheduleEditor";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../components/ui/card";
import {
    ArrowLeft,
    Loader2,
    Save,
    Trash2,
    CheckCircle2,
    XCircle,
    Banknote,
    Calendar,
    FileSignature,
    User,
    Eye,
    ScrollText,
} from "lucide-react";
import { toast } from "sonner";

const TYPES = [
    { value: "ACCONTO", label: "Acconto" },
    { value: "FNG", label: "F.N.G." },
    { value: "TFR", label: "T.F.R." },
    { value: "PRESTITO", label: "Prestito" },
    { value: "ANF", label: "A.N.F." },
    { value: "EMOLUMENTI", label: "Emolumenti" },
    { value: "ACCONTO_FERIE_NON_GODUTE", label: "Acconto ferie non godute" },
    { value: "ACCONTO_TFR", label: "Acconto T.F.R." },
];

const METHODS = [
    { value: "BONIFICO", label: "Bonifico" },
    { value: "BON_ISTAN", label: "Bonifico istantaneo" },
    { value: "ASSEGNO", label: "Assegno" },
    { value: "CONTANTI", label: "Contanti" },
];

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("it-IT", {
            day: "2-digit", month: "2-digit", year: "numeric",
        });
    } catch { return iso; }
};


export default function AdvanceDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = !!user && user.role === "admin";
    const [advance, setAdvance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [edit, setEdit] = useState({});
    const [previewOpen, setPreviewOpen] = useState(false);
    const [rows, setRows] = useState([]);
    const [employees, setEmployees] = useState([]);

    useEffect(() => {
        api.get("/employees").then((r) => {
            const list = [...r.data].sort((a, b) => `${a.cognome} ${a.nome}`.localeCompare(`${b.cognome} ${b.nome}`));
            setEmployees(list);
        }).catch((e) => console.warn("Caricamento dipendenti fallito:", e?.message));
    }, []);

    const nRates = parseInt(edit.recovery_rates, 10) || 1;
    const amountNum = parseFloat(edit.amount) || 0;
    const kRec = advance ? Math.min(advance.installments_recovered || 0, nRates) : 0;

    // Build per-rata rows (bp + amount) from the advance data / rate count
    useEffect(() => {
        if (!advance || nRates <= 1) {
            setRows([]);
            return;
        }
        const n = nRates;
        const total = parseFloat(edit.amount) || advance.amount;
        const kr = Math.min(advance.installments_recovered || 0, n);
        const sched = advance.installment_schedule || [];
        const plan = advance.installment_plan || [];
        const baseBp = advance.recovery_payslip || "";

        const consecutive = buildConsecutiveLabels(baseBp, n);
        const lockedLabels = Array.from({ length: kr }, (_, i) => sched[i] || consecutive[i] || "");
        const lockedAmounts = Array.from({ length: kr }, (_, i) =>
            plan[i] != null ? round2(plan[i]) : round2(advance.installments_amount || total / n)
        );

        let labels;
        if (sched.length === n) {
            labels = [...sched];
        } else {
            const startAfter = kr > 0 ? nextBpLabel(lockedLabels[kr - 1]) : baseBp;
            labels = [...lockedLabels, ...buildConsecutiveLabels(startAfter, n - kr)];
        }
        let amounts;
        if (plan.length === n) {
            amounts = plan.map((x) => String(round2(x)));
        } else {
            const lockedSum = round2(lockedAmounts.reduce((a, b) => a + b, 0));
            const split = evenSplit(round2(total - lockedSum), n - kr);
            amounts = [...lockedAmounts.map(String), ...split.map(String)];
        }
        setRows(labels.map((bp, i) => ({ bp, amount: amounts[i] })));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [advance, nRates]);

    const lockedSum = round2(rows.slice(0, kRec).reduce((a, r) => a + (parseFloat(r.amount) || 0), 0));
    const residual = round2(amountNum - lockedSum);
    const mRemaining = nRates - kRec;
    const proposals = useMemo(() => {
        if (mRemaining < 2 || residual <= 0) return [];
        return buildPlanProposals(residual, mRemaining);
    }, [residual, mRemaining]);

    const applyProposal = (p) => {
        setRows((rs) => rs.map((r, i) => (i < kRec ? r : { ...r, amount: String(p[i - kRec]) })));
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/advances/${id}`);
            setAdvance(data);
            setEdit({
                employee_id: data.employee_id,
                amount: data.amount,
                date: data.date,
                type: data.type,
                payment_method: data.payment_method,
                payment_account: data.payment_account || "",
                bank_name: data.bank_name || "",
                recovery_payslip: data.recovery_payslip || "",
                recovery_rates: data.recovery_rates,
                notes: data.notes || "",
            });
        } catch (e) {
            toast.error("Acconto non trovato");
            navigate("/acconti");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                ...edit,
                amount: parseFloat(edit.amount),
                recovery_rates: parseInt(edit.recovery_rates, 10) || 1,
            };
            if (nRates > 1) {
                const err = validateScheduleRows(rows, payload.amount);
                if (err) {
                    toast.error(err);
                    setSaving(false);
                    return;
                }
                payload.installment_plan = rows.map((r) => round2(parseFloat(r.amount)));
                payload.installment_schedule = rows.map((r) => r.bp);
            } else {
                payload.installment_plan = null;
                payload.installment_schedule = null;
            }
            const { data } = await api.patch(`/advances/${id}`, payload);
            setAdvance(data);
            setEdit((f) => ({ ...f, recovery_payslip: data.recovery_payslip || "" }));
            toast.success("Modifiche salvate");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore salvataggio");
        } finally {
            setSaving(false);
        }
    };

    const setStatus = async (newStatus) => {
        try {
            const { data } = await api.patch(`/advances/${id}`, { status: newStatus });
            setAdvance(data);
            toast.success(`Stato aggiornato a "${newStatus}"`);
        } catch (e) {
            toast.error("Errore");
        }
    };

    const remove = async () => {
        if (!window.confirm("Eliminare questa ricevuta? L'operazione è irreversibile.")) return;
        try {
            await api.delete(`/advances/${id}`);
            toast.success("Ricevuta eliminata");
            navigate("/acconti");
        } catch (e) {
            toast.error("Errore eliminazione");
        }
    };

    if (loading || !advance) {
        return (
            <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
        );
    }

    const scheduleSummary = advance.installment_schedule
        ? `rate su: ${advance.installment_schedule.map((s) => s.replace("B.P. ", "")).join(", ")}`
        : advance.recovery_rates > 1
            ? `in ${advance.recovery_rates} rate`
            : "unica soluzione";

    const canEdit = isAdmin && !advance.is_fully_recovered;

    return (
        <div className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8" data-testid="advance-detail-page">
            <div className="flex items-center justify-between">
                <Button
                    variant="ghost"
                    onClick={() => navigate("/acconti")}
                    className="gap-2"
                    data-testid="back-btn"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Torna all'elenco
                </Button>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        onClick={() => setPreviewOpen(true)}
                        className="gap-2 border-[#15498F] text-[#15498F] hover:bg-[#5BAEDB]/10"
                        data-testid="pdf-btn"
                    >
                        <Eye className="h-4 w-4" />
                        Anteprima ricevuta
                    </Button>
                    {["TFR", "ACCONTO_TFR"].includes(advance.type) && (
                        <Button
                            variant="outline"
                            onClick={async () => {
                                try {
                                    const r = await api.get(`/advances/${advance.id}/tfr-declaration/pdf`, { responseType: "blob" });
                                    const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                                    window.open(url, "_blank");
                                } catch {
                                    toast.error("Errore generazione dichiarazione TFR");
                                }
                            }}
                            className="gap-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                            data-testid="tfr-declaration-btn"
                            title="Dichiarazione di anticipazione sul T.F.R. (art. 2120 c.c.) da firmare insieme alla ricevuta"
                        >
                            <ScrollText className="h-4 w-4" />
                            Dichiarazione TFR
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        onClick={remove}
                        className="gap-2 border-red-300 text-red-700 hover:bg-red-50"
                        data-testid="delete-btn"
                    >
                        <Trash2 className="h-4 w-4" />
                        Elimina
                    </Button>
                </div>
            </div>

            {/* Receipt-style header card */}
            <Card className="overflow-hidden">
                <div className="bg-gradient-to-br from-[#15498F] to-[#376DB3] px-6 py-5 text-white">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-white/70">
                                Ricevuta acconto
                            </div>
                            <div className="mt-1 font-display text-3xl font-bold">
                                #{advance.receipt_label}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-[10px] font-semibold uppercase tracking-widest text-white/70">Importo</div>
                            <div className="font-display text-3xl font-bold">{fmtEur(advance.amount)}</div>
                            <div className="text-xs italic text-white/80">{advance.amount_in_words}</div>
                        </div>
                    </div>
                </div>
                <CardContent className="space-y-4 pt-4">
                    {advance.legacy_import && (
                        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                            Ricevuta importata dal libro acconti cartaceo (Excel "Acconti 2026.xlsm").
                        </div>
                    )}
                    <div className="grid gap-3 md:grid-cols-3">
                        <InfoRow icon={User} label="Dipendente" value={
                            <Link to={`/dipendenti/${advance.employee_id}`} className="font-medium text-[#376DB3] hover:underline">
                                {advance.employee_label}
                            </Link>
                        } extra={`mat. ${advance.matricola || "—"}`} />
                        <InfoRow icon={Calendar} label="Data ricevuta" value={fmtDate(advance.date)} />
                        <InfoRow icon={Banknote} label="Tipo" value={advance.type_label} />
                        <InfoRow label="Modalità pagamento" value={advance.payment_method_label} />
                        <InfoRow label="Dettaglio pagamento" value={advance.payment_account || "—"} />
                        <InfoRow label="Recupero da B.P." value={
                            advance.recovery_payslip || "—"
                        } extra={scheduleSummary} />
                    </div>
                </CardContent>
            </Card>

            {/* Status + workflow */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold">Stato e firma</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="flex flex-wrap items-center gap-3">
                        <Badge variant="outline" className="px-3 py-1 text-sm">
                            Stato: <span className="ml-1 font-semibold">{advance.status}</span>
                        </Badge>
                        {advance.signed_at && (
                            <div className="text-xs text-emerald-700">
                                <CheckCircle2 className="inline h-3 w-3" /> Firmato il {fmtDate(advance.signed_at)}
                            </div>
                        )}
                        {["TFR", "ACCONTO_TFR"].includes(advance.type) && (
                            <div className={`text-xs ${advance.tfr_decl_signed_at ? "text-emerald-700" : "text-amber-700"}`} data-testid="tfr-decl-status">
                                {advance.tfr_decl_signed_at ? (
                                    <><CheckCircle2 className="inline h-3 w-3" /> Dichiarazione TFR firmata il {fmtDate(advance.tfr_decl_signed_at)}</>
                                ) : (
                                    <>Dichiarazione TFR non ancora firmata dal dipendente</>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={advance.status === "signed"}
                            onClick={() => setStatus("signed")}
                            data-testid="set-signed"
                            className="gap-2"
                        >
                            <FileSignature className="h-3.5 w-3.5" />
                            Marca come firmato
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={advance.status === "recovered"}
                            onClick={() => setStatus("recovered")}
                            data-testid="set-recovered"
                            className="gap-2 border-slate-300 text-slate-700"
                        >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Marca come recuperato
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={advance.status === "cancelled"}
                            onClick={() => setStatus("cancelled")}
                            data-testid="set-cancelled"
                            className="gap-2 border-red-300 text-red-700 hover:bg-red-50"
                        >
                            <XCircle className="h-3.5 w-3.5" />
                            Annulla
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Edit form — admin-only, and only while not fully recovered */}
            {canEdit ? (
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold">Modifica dati</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="grid gap-3 md:grid-cols-2">
                        <div className="md:col-span-2">
                            <Label className="text-xs">Intestatario (dipendente) *</Label>
                            <Select value={edit.employee_id} onValueChange={(v) => setEdit((f) => ({ ...f, employee_id: v }))}>
                                <SelectTrigger className="mt-1" data-testid="edit-employee"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                                <SelectContent className="max-h-72">
                                    {employees.map((e) => (
                                        <SelectItem key={e.id} value={e.id}>
                                            {e.cognome} {e.nome} (mat. {e.matricola || "—"}){e.data_cessazione ? " — cessato" : ""}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Importo (€) *</Label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={edit.amount || ""}
                                onChange={(e) => setEdit((f) => ({ ...f, amount: e.target.value }))}
                                className="mt-1"
                                data-testid="edit-amount"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Data ricevuta *</Label>
                            <Input
                                type="date"
                                value={edit.date || ""}
                                onChange={(e) => setEdit((f) => ({ ...f, date: e.target.value }))}
                                className="mt-1"
                                data-testid="edit-date"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Tipo</Label>
                            <Select value={edit.type} onValueChange={(v) => setEdit((f) => ({ ...f, type: v }))}>
                                <SelectTrigger className="mt-1" data-testid="edit-type"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Modalità pagamento</Label>
                            <Select value={edit.payment_method} onValueChange={(v) => setEdit((f) => ({ ...f, payment_method: v }))}>
                                <SelectTrigger className="mt-1" data-testid="edit-method"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Dettaglio pagamento</Label>
                            <Input
                                value={edit.payment_account || ""}
                                onChange={(e) => setEdit((f) => ({ ...f, payment_account: e.target.value }))}
                                className="mt-1"
                                data-testid="edit-account"
                            />
                        </div>
                        {nRates <= 1 ? (
                            <div>
                                <Label className="text-xs">Busta paga di recupero</Label>
                                <div className="mt-1">
                                    <BustaPagaSelector
                                        value={edit.recovery_payslip || ""}
                                        onChange={(v) => setEdit((f) => ({ ...f, recovery_payslip: v }))}
                                        testidPrefix="edit"
                                    />
                                </div>
                                <p className="mt-1 text-[10px] text-slate-500">
                                    14 buste paga: 12 mensili + <b>XIII</b> (tredicesima) + <b>XIV</b> (quattordicesima)
                                </p>
                            </div>
                        ) : (
                            <div>
                                <Label className="text-xs">Busta paga di recupero</Label>
                                <p className="mt-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600" data-testid="edit-schedule-hint">
                                    Con più rate, la busta paga di <b>ogni rata</b> si imposta nel
                                    <b> Piano rate</b> qui sotto (anche mesi non consecutivi, XIII/XIV incluse).
                                </p>
                            </div>
                        )}
                        <div>
                            <Label className="text-xs">Numero rate</Label>
                            <Input
                                type="number"
                                min="1"
                                max="24"
                                value={edit.recovery_rates || 1}
                                onChange={(e) => setEdit((f) => ({ ...f, recovery_rates: e.target.value }))}
                                className="mt-1"
                                data-testid="edit-rates"
                            />
                        </div>
                        {nRates > 1 && (
                            <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 md:col-span-2" data-testid="edit-plan-box">
                                <Label className="text-xs font-semibold text-slate-700">Piano rate — mese e importo di ogni rata</Label>
                                {kRec > 0 && (
                                    <p className="mt-0.5 text-[11px] text-slate-600" data-testid="edit-plan-recovered-info">
                                        Già trattenute <b>{kRec}</b> rate per <b>{fmtEur(lockedSum)}</b> —
                                        residuo <b>{fmtEur(residual)}</b> su <b>{mRemaining}</b> rate future.
                                        Le rate passate sono bloccate sul mese; puoi correggerne l'importo se la
                                        trattenuta reale è stata diversa.
                                    </p>
                                )}
                                <div className="mt-2">
                                    <InstallmentScheduleEditor
                                        rows={rows}
                                        onChange={setRows}
                                        lockedCount={kRec}
                                        totalAmount={amountNum}
                                        testidPrefix="edit-sched"
                                    />
                                </div>
                                {proposals.length > 0 && (
                                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Importi rapidi:</span>
                                        {proposals.map((p, i) => (
                                            <button
                                                key={planLabel(p)}
                                                type="button"
                                                onClick={() => applyProposal(p)}
                                                className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[11px] text-slate-700 transition-colors hover:border-[#376DB3] hover:text-[#376DB3]"
                                                data-testid={`edit-plan-option-${i}`}
                                            >
                                                {planLabel(p)}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="md:col-span-2">
                            <Label className="text-xs">Note</Label>
                            <Input
                                value={edit.notes || ""}
                                onChange={(e) => setEdit((f) => ({ ...f, notes: e.target.value }))}
                                className="mt-1"
                                data-testid="edit-notes"
                            />
                        </div>
                    </div>
                    <Button onClick={save} disabled={saving} className="gap-2 bg-[#15498F] text-white hover:bg-[#15498F]/90" data-testid="save-btn">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Salva modifiche
                    </Button>
                </CardContent>
            </Card>
            ) : (
            <Card>
                <CardContent className="flex items-center gap-2 py-4 text-sm text-slate-500" data-testid="edit-locked-info">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-slate-400" />
                    {isAdmin
                        ? "Acconto interamente recuperato: i dati non sono più modificabili."
                        : "La modifica dei dati degli acconti è riservata all'amministratore."}
                </CardContent>
            </Card>
            )}

            <AdvancePdfPreviewDialog
                advance={advance}
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
            />
        </div>
    );
}


function InfoRow({ icon: Icon, label, value, extra }) {
    return (
        <div>
            <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                {Icon && <Icon className="h-3 w-3" />}
                {label}
            </div>
            <div className="mt-0.5 font-medium text-slate-800">{value}</div>
            {extra && <div className="text-xs text-slate-500">{extra}</div>}
        </div>
    );
}
